import {
  DatabaseOutlined,
  DeleteOutlined,
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import {
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import client from '@/api/client'
import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE } from '@/components/common/MonoId'
import { ResourceBillingTab } from '@/components/common/ResourceBillingTab'
import { StatusTag } from '@/components/common/StatusTag'
import { TerminateConfirmModal } from '@/components/common/TerminateConfirmModal'
import { TroubleshootCard } from '@/components/common/TroubleshootCard'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { VM_TERMINAL_STATUSES } from '@/constants/statuses'
import { useDetailTermination } from '@/hooks/useDetailTermination'
import { useVmConfigurations } from '@/hooks/useVmConfigurations'
import type { PaginatedItems } from '@/types/api'

import { VmBillingCard } from './components/VmBillingCard'
import { VmPublicIpField } from './components/VmPublicIpField'

type UserVmDto = components['schemas']['AdminUserVmDto']
type UserStorageDto = components['schemas']['UserStorageDto']
type VmConfigurationDto = components['schemas']['VmConfigurationDto']

export default function ComputeShowPage() {
  const { id: vmId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'info'
  const { configurations, query: configurationsQuery } = useVmConfigurations()

  const vmQuery = useCustom<PaginatedItems<UserVmDto>>({
    url: `${VODOPAD_ENDPOINT}/v2/admin/vms`,
    method: 'get',
    config: { query: { 'ids[]': vmId, page: 1, perPage: 1 } },
    queryOptions: { enabled: Boolean(vmId) },
  })

  const { terminateOpen, openTerminate, closeTerminate, handleTerminate } =
    useDetailTermination({
      resourceLabel: 'VM',
      terminateFn: () =>
        client.POST('/v1/admin/vms/terminate', {
          body: { vmIds: [vmId ?? ''] },
        }),
      onSuccess: () => void vmQuery.query.refetch(),
    })

  const vm = (vmQuery.query.data?.data as PaginatedItems<UserVmDto> | undefined)
    ?.items?.[0]

  if (!vmId) return null

  if (vmQuery.query.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!vm) {
    return <div style={{ textAlign: 'center', padding: 48 }}>VM not found</div>
  }

  const configuration = configurations.find(
    (item) => item.id === vm.configurationId,
  )

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: <Link to="/vms">Compute</Link> }, { title: vm.name }]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>
          VM: <Typography.Text copyable>{vm.name}</Typography.Text>
        </h2>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={openTerminate}
          disabled={VM_TERMINAL_STATUSES.has(vm.status)}
        >
          Terminate
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        items={[
          {
            key: 'info',
            label: 'Info',
            icon: <InfoCircleOutlined />,
            children: (
              <VmInfoTab
                vm={vm}
                configuration={configuration}
                isConfigurationLoading={configurationsQuery.isLoading}
                onChanged={() => void vmQuery.query.refetch()}
              />
            ),
          },
          {
            key: 'disks',
            label: `Disks (${(vm.bootDisk ? 1 : 0) + vm.dataDisks.length})`,
            icon: <DatabaseOutlined />,
            children: <VmDisksTab vm={vm} />,
          },
          {
            key: 'billing',
            label: 'Billing',
            icon: <DollarOutlined />,
            children: (
              <ResourceBillingTab userId={vm.userId} resourceId={vmId} />
            ),
          },
        ]}
      />

      <TerminateConfirmModal
        open={terminateOpen}
        title="Terminate VM"
        resourceName={vm.name}
        onConfirm={handleTerminate}
        onCancel={closeTerminate}
      />
    </div>
  )
}

function VmInfoTab({
  vm,
  configuration,
  isConfigurationLoading,
  onChanged,
}: {
  vm: UserVmDto
  configuration: VmConfigurationDto | undefined
  isConfigurationLoading: boolean
  onChanged: () => void
}) {
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <Card title="VM Info">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="ID">
            <Typography.Text copyable style={MONO_STYLE}>
              {vm.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            <Typography.Text copyable>{vm.name}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <StatusTag status={vm.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Restart Required">
            {vm.restartRequired ? 'Yes' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="User">
            <UserEmailLink userId={vm.userId} />
          </Descriptions.Item>
          <Descriptions.Item label="Cluster">
            <ClusterNameLink clusterId={vm.clusterId} />
          </Descriptions.Item>
          <Descriptions.Item label="Public IP">
            <VmPublicIpField vm={vm} onChanged={onChanged} />
          </Descriptions.Item>
          <Descriptions.Item label="SSH Keys">
            {vm.sshKeys.length > 0
              ? vm.sshKeys.map((key) => (
                  <div key={key}>
                    <Typography.Text copyable style={MONO_STYLE}>
                      {key}
                    </Typography.Text>
                  </div>
                ))
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Subnets">
            {vm.subnets.length > 0
              ? vm.subnets.map((subnet) => (
                  <div key={subnet}>
                    <Typography.Text copyable style={MONO_STYLE}>
                      {subnet}
                    </Typography.Text>
                  </div>
                ))
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            <FormattedDate date={vm.createdAt} fmt="PPp" />
          </Descriptions.Item>
          <Descriptions.Item label="Ready Since">
            {vm.readySince ? (
              <FormattedDate date={vm.readySince} fmt="PPp" />
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            <FormattedDate date={vm.updatedAt} fmt="PPp" />
          </Descriptions.Item>
          <Descriptions.Item label="Last Paid">
            <FormattedDate date={vm.lastPaidAt} fmt="PPp" />
          </Descriptions.Item>
          <Descriptions.Item label="Paid Until">
            <FormattedDate date={vm.paidUntil} fmt="PPp" />
          </Descriptions.Item>
          <Descriptions.Item label="Terminated">
            <FormattedDate date={vm.terminatedAt} fmt="PPp" />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <VmBillingCard vmId={vm.id} />

      <VmConfigurationCard
        configurationId={vm.configurationId}
        configuration={configuration}
        isLoading={isConfigurationLoading}
      />

      <TroubleshootCard kind="vm" userId={vm.userId} resourceId={vm.id} />
    </div>
  )
}

function VmConfigurationCard({
  configurationId,
  configuration,
  isLoading,
}: {
  configurationId: string
  configuration: VmConfigurationDto | undefined
  isLoading: boolean
}) {
  let dedicatedLabel = '—'
  if (configuration) {
    dedicatedLabel = configuration.dedicated ? 'Yes' : 'No'
  }

  return (
    <Card title="Configuration">
      {isLoading && !configuration ? (
        <Spin />
      ) : (
        <Descriptions column={1} size="small">
          <Descriptions.Item label="ID">
            <Typography.Text copyable style={MONO_STYLE}>
              {configuration?.id ?? configurationId}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            {configuration?.name ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Slug">
            {configuration?.slug ? <Tag>{configuration.slug}</Tag> : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {configuration?.description ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Preset">
            {configuration?.preset ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="vCPU">
            {configuration?.vcpu ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="RAM (GB)">
            {configuration?.ramGb ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="CPU Families">
            {configuration?.cpuFamilies.length
              ? configuration.cpuFamilies.map((family) => (
                  <Tag key={family}>{family}</Tag>
                ))
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Dedicated">
            {dedicatedLabel}
          </Descriptions.Item>
          <Descriptions.Item label="Tags">
            {configuration?.tags.length
              ? configuration.tags.map((tag) => (
                  <Tag key={tag} color="blue">
                    {tag}
                  </Tag>
                ))
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  )
}

function DiskRow({ diskId, role }: { diskId: string; role: string }) {
  const storageQuery = useCustom<PaginatedItems<UserStorageDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/storages?ids[]=${diskId}&page=1&perPage=1`,
    method: 'get',
    queryOptions: { staleTime: 5 * 60 * 1000 },
  })

  const storage = (
    storageQuery.query.data?.data as PaginatedItems<UserStorageDto> | undefined
  )?.items?.[0]

  return {
    key: diskId,
    id: diskId,
    name:
      storage?.name ??
      (storageQuery.query.isLoading
        ? 'Loading...'
        : diskId.slice(0, 8) + '...'),
    role,
    type: storage?.storageType ?? '—',
    volumeGb: storage?.volumeGb ?? '—',
    status: storage?.status ?? '—',
  }
}

function VmDisksTab({ vm }: { vm: UserVmDto }) {
  const allDiskIds = [
    ...(vm.bootDisk ? [{ id: vm.bootDisk, role: 'Boot' }] : []),
    ...vm.dataDisks.map((id) => ({ id, role: 'Data' })),
  ]

  // Fetch each disk's storage details
  const rows = allDiskIds.map(({ id, role }) => DiskRow({ diskId: id, role }))

  return (
    <Table
      dataSource={rows}
      rowKey="id"
      pagination={false}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, record: { id: string }) => (
            <Link
              to={`/storage/${record.id}`}
              style={{
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                textUnderlineOffset: 4,
              }}
            >
              <Typography.Text copyable={{ text: record.id }}>
                {name}
              </Typography.Text>
            </Link>
          ),
        },
        {
          title: 'Role',
          dataIndex: 'role',
          key: 'role',
          width: 80,
          render: (role: string) => (
            <Tag color={role === 'Boot' ? 'blue' : 'default'}>{role}</Tag>
          ),
        },
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
          width: 80,
        },
        {
          title: 'Volume (GB)',
          dataIndex: 'volumeGb',
          key: 'volumeGb',
          width: 110,
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 100,
          render: (s: string) => (s !== '—' ? <StatusTag status={s} /> : '—'),
        },
      ]}
    />
  )
}
